import { getFullURL } from './endpoints';
import { startsWith } from 'lodash';
import { emitNotification } from '../components/Notification';
import i18next from 'i18next';
import { showUpgradeModel } from '../util';
import { LogoutOptions, useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo, useState } from 'react';
import { ToastOptions } from 'react-toastify';
import { useNavigate } from 'react-router';
import { ErrorDto } from '../../../shared/utils/error.dto';
import { TherapistWithCreditsDto } from '../../../shared/dtos/therapist.dto';

export interface APIOptions {
  token?: string;
  body?: any;
  method?: string;
  type?: string;
}

export interface APICallbacks {
  logout?: (options?: LogoutOptions) => Promise<void>;
}

// class RestAPIError extends Error {
//   code;

//   constructor(message, errorCode) {
//     super(message);
//     this.code = errorCode;
//     this.name = 'RestAPIError';
//   }
// }

export const logout = (callbacks?: APICallbacks): Promise<void> => {
  if (callbacks && callbacks.logout) {
    return logout();
  }
  return Promise.resolve();
}

export async function fetchFromRestAPI(path: string, options?: APIOptions, callbacks?: APICallbacks) {
  let fetchRequestConfig = {
    method: options && options.method ? options.method : 'GET',
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': '0',
      'User-Language': i18next.language,
    },
  };

  if (options && options.body) {
    if (options.body instanceof FormData) {
      fetchRequestConfig = Object.assign({}, fetchRequestConfig, {
        body: options.body,
      });
    } else {
      fetchRequestConfig = Object.assign({}, fetchRequestConfig, {
        body: JSON.stringify(options.body),
        headers: {
          ...fetchRequestConfig.headers,
          'Content-Type': 'application/json',
        }
      });
    }
  }

  if (options && options.token) {
    fetchRequestConfig.headers = Object.assign({}, fetchRequestConfig.headers, {
      Authorization: `Bearer ${options.token}`,
    });
  }

  const fetchRequest = await fetch(getFullURL(path, options && options.type && options.type), fetchRequestConfig);
  const responseText = await fetchRequest.text();

  const isApplicationJson = startsWith(fetchRequest.headers.get('content-type') ?? undefined, 'application/json');

  const errorNtfnSettings: ToastOptions = {
    type: 'error',
    position: 'top-right'
  };

  switch (fetchRequest.status) {
    case 401:
      emitNotification(i18next.t('errorMessageTemplate.authError'), errorNtfnSettings);
      await logout(callbacks);
      break;
    case 451:
      if (responseText) {
        if (isApplicationJson) {
          const { error } = JSON.parse(responseText);
          if (error && error.startsWith("key_")) {
            const key = "errorMessageTemplate." + error;
            showUpgradeModel(true, i18next.t(`${key}`), i18next.t('errorMessageTemplate.key_TherapistPlanUpgradeError'));
          }
        }
      }
      break;
    case 500:
      emitNotification(i18next.t('errorMessageTemplate.genericMsg'), errorNtfnSettings);
      break;
    default:
      if (responseText && JSON.parse(responseText)) {
        if (isApplicationJson) {
          const { error } = JSON.parse(responseText);
          if (error && error === 'key_PlanExpried') {
            if (error.startsWith("key_")) {
              const key = "errorMessageTemplate." + error;
              emitNotification(i18next.t(`${key}`), errorNtfnSettings);
            } else {
              emitNotification(error, errorNtfnSettings);
            }
            setTimeout(() => { logout(callbacks) }, 2000);
          } else if (error && error !== 'key_NoProfileFound') {
            if (error.startsWith("key_")) {
              const key = "errorMessageTemplate." + error;
              emitNotification(i18next.t(`${key}`), errorNtfnSettings);
            } else {
              emitNotification(error, errorNtfnSettings);
            }
          }
        } else {
          emitNotification(i18next.t('errorMessageTemplate.genericMsg'), errorNtfnSettings);
        }
      }
      break;
  }

  if (responseText) {
    return isApplicationJson ? JSON.parse(responseText) : responseText;
  }
}

export type AuthFetch = (path: string, options?: APIOptions) => Promise<any>;
export type AuthFetchRaw = (input: string | URL | Request, init?: RequestInit) => Promise<Response>;

export const useAuthFetchFromRestAPI = () => {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  const logoutAndRedirect = async (options?: LogoutOptions): Promise<void> => {
    await logout(options);
    return navigate('/');
  };

  const authFetch: AuthFetch = useMemo(() => {
    return async (path: string, options?: APIOptions): Promise<any> => {
      const token = options?.token ?? await getAccessTokenSilently();
      const privateOptions = options ? { ...options, token } : { token };

      return fetchFromRestAPI(path, privateOptions, { logout: logoutAndRedirect })
    }

  }, [getAccessTokenSilently, logout, isAuthenticated])

  const authFetchRaw: AuthFetchRaw = useMemo(() => {
    return async (input: string | URL | Request, options: RequestInit = {}): Promise<Response> => {
      const token = await getAccessTokenSilently();
      const authHeaders = {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      }
      const finalOptions = {
        ...options,
        headers: authHeaders,
      }
      return fetch(input, finalOptions);
    }
  }, [getAccessTokenSilently, isAuthenticated])

  return { authFetch, authFetchRaw };
}

// TODO: WIP and r.f.u.: Change API responses to { data: TResponse | null; error: ErrorDto | null; }
function useAsync<TArgs, TResponse>(asyncFunction: (args: TArgs) => Promise<TResponse | ErrorDto>): AsyncState<TArgs, TResponse> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorDto | null>(null);
  const [data, setData] = useState<TResponse | null>(null);

  const load = useCallback(async (args: TArgs): Promise<TResponse | ErrorDto> => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const response = await asyncFunction(args);
      if (response && typeof response === 'object' && 'error' in response) {
        setError({ error: response.error });
        return { error: response.error };
      }
      setData(response);
      return response;
    } catch (err) {
      setError({ error: (err as Error).message });
      return { error: (err as Error).message };
    } finally {
      setLoading(false);
    }
  }, [asyncFunction]);

  return { load, loading, error, data };
}

export function useTherapists(): AsyncState<number, TherapistWithCreditsDto[]> {
  const { authFetch } = useAuthFetchFromRestAPI();
  const fetchCall = async (page: number): Promise<TherapistWithCreditsDto[] | ErrorDto> => {
    return authFetch('/api/v1/admin/therapists/' + page, {method: 'GET'});
  }
  return useAsync(fetchCall);
}

export type AsyncState<TArgs, TData> = {
  load: (args: TArgs) => Promise<TData | ErrorDto>,
  loading: boolean | null,
  error: ErrorDto | null,
  data: TData | null };
